import { Log_WorkoutSet } from '@cf-shared/data-model/log-workout';
import {
  cellSummaryTexts, OverviewCell, OverviewCellKind, OverviewIntensityTag, OverviewMark, OverviewRow, OverviewSection, OverviewSessionTag,
  OverviewWindowKind, TRAINING_OVERVIEW_PAGE_SIZE, TrainingOverviewModel,
} from 'services/training-overview';
import { Formatting } from 'util/formatting';

// A hand-written Training Overview for the Test UI Components page (#5456, redesigned in #6676):
// BORI's Workout A from the walkthrough in design-docs/coaching-dashboard.md. It holds every kind of
// cell, both marks, every session tag, a heavy/light row, a weight range, a grey row and an "Other
// sessions" table, so each can be looked at without finding an account whose history has it. Each
// row is written for 7 sessions; the card shows the newest 4, as the real page opens, and a phone
// row shows its newest non-empty one.

const EMPTY: OverviewCell = {
  kind: OverviewCellKind.Empty, setsText: '', weightText: '', repsText: '',
  intensityTag: OverviewIntensityTag.None, sessionTag: OverviewSessionTag.None, mark: OverviewMark.None,
};
const SKIPPED: OverviewCell = { ...EMPTY, kind: OverviewCellKind.Skipped };

/**
 * a trained cell written exactly as the app writes it: `cellSummaryTexts` over sets of the given
 * (weight, reps) groups, with the no-break spaces the real formatter uses (#6691)
 */
function formatted(groups: [number, number[]][], extra: Partial<OverviewCell> = {}): OverviewCell {
  const sets = groups.flatMap(([weight_kg, reps]) => reps.map(rep => ({ stats: { weight_kg, reps: rep } }) as Log_WorkoutSet));
  const setsText = groups.map(([kg, reps]) => `${kg}${Formatting.thinSpace}kg${Formatting.times}${reps.join(Formatting.repSeparator)}`)
    .join(Formatting.setGroupSeparator);
  const texts = cellSummaryTexts(setsText, sets, set => `${set.stats.weight_kg}${Formatting.thinSpace}kg`,
    { unit: Formatting.thinSpace, rep: Formatting.repSeparator, plus: Formatting.plus });
  return { ...EMPTY, kind: OverviewCellKind.Trained, ...texts, ...extra };
}

/** a trained cell with myo-rep mini-sets, written as the reps line shows them: "13 + 4 + 3" */
function myo(kg: number, repsText: string, extra: Partial<OverviewCell> = {}): OverviewCell {
  const weightText = `${kg}${Formatting.thinSpace}kg`;
  return { ...EMPTY, kind: OverviewCellKind.Trained, setsText: `${weightText}${Formatting.times}${repsText}`, weightText, repsText, ...extra };
}

const heavy = { intensityTag: OverviewIntensityTag.Heavy };
const light = { intensityTag: OverviewIntensityTag.Light };
const same = { mark: OverviewMark.Same };
const down = { mark: OverviewMark.Down };

/** a row of 7 cells, one per session of `section` below, oldest first */
interface SampleRow {
  exerciseId: string;
  name: string;
  setsText?: string;
  repTargetText?: string;
  isInProgram: boolean;
  isExerciseMissing?: boolean;
  cells: OverviewCell[];
}

/** one table as the page opens it: the newest 4 of its sessions shown, each row's newest non-empty cell for the phone */
function section(planReference: number | undefined, title: string, dates: string[], rows: SampleRow[]): OverviewSection {
  const firstShownIndex = Math.max(0, dates.length - TRAINING_OVERVIEW_PAGE_SIZE);
  const toRow = (row: SampleRow): OverviewRow => {
    const newestAt = row.cells.map(cell => cell.kind !== OverviewCellKind.Empty).lastIndexOf(true);
    return {
      exerciseId: row.exerciseId,
      name: row.name,
      isInProgram: row.isInProgram,
      isExerciseMissing: !!row.isExerciseMissing,
      subSlotId: row.isInProgram ? `${row.exerciseId}-slot` : undefined,
      setsText: row.setsText,
      repTargetText: row.repTargetText,
      cells: row.cells.slice(firstShownIndex),
      lastSession: newestAt >= 0 ? { dateIso: dates[newestAt], cell: row.cells[newestAt] } : undefined,
    };
  };
  return {
    planReference,
    title,
    sessionCount: dates.length,
    sessionIds: dates.map(dateIso => `log/${dateIso}`),
    firstShownIndex,
    window: { kind: OverviewWindowKind.Newest },
    columns: dates.slice(firstShownIndex).map(dateIso => ({ workoutId: `log/${dateIso}`, dateIso })),
    programRows: rows.filter(row => row.isInProgram).map(toRow),
    greyRows: rows.filter(row => !row.isInProgram).map(toRow),
  };
}

const program = (exerciseId: string, name: string, setsText: string, repTargetText: string, cells: OverviewCell[]): SampleRow =>
  ({ exerciseId, name, setsText, repTargetText, isInProgram: true, cells });
const grey = (exerciseId: string, name: string, cells: OverviewCell[], isExerciseMissing = false): SampleRow =>
  ({ exerciseId, name, isInProgram: false, isExerciseMissing, cells });

const DATES_A = ['2026-08-24', '2026-08-31', '2026-09-07', '2026-09-14', '2026-09-21', '2026-09-28', '2026-10-05'];

export const SAMPLE_TRAINING_OVERVIEW: TrainingOverviewModel = {
  latestSameCount: 1,
  latestDownCount: 0,
  sections: [
    section(0, 'Workout A', DATES_A, [
      program('squat', 'Back Squat', '3', '8', [
        EMPTY, EMPTY,
        formatted([[120, [3]]], { sessionTag: OverviewSessionTag.Test }),
        formatted([[105, [8, 7, 6]]]),
        formatted([[100, [8, 8, 7]]], { sessionTag: OverviewSessionTag.Break }),
        formatted([[110, [7, 6, 5]]]),
        formatted([[110, [8, 7, 6]]]),
      ]),
      program('bench', 'Bench Press', '3', '6/10', [
        formatted([[67.5, [10, 9, 8]]], light),
        formatted([[82.5, [5, 4, 4]]], heavy),
        formatted([[70, [8, 8, 7]]], light),
        formatted([[82.5, [5, 4, 4]]], { ...heavy, ...same }),
        formatted([[70, [9, 8, 7]]], light),
        SKIPPED,
        formatted([[70, [9, 8, 8]]], { ...light, ...same }),
      ]),
      program('rdl', 'Romanian Deadlift', '3', '10', [
        formatted([[85, [8, 7, 6]]]),
        formatted([[85, [9, 8, 7]]]),
        formatted([[85, [10, 8, 8]]], { sessionTag: OverviewSessionTag.Cap }),
        formatted([[90, [8, 7, 7]]]),
        formatted([[90, [7, 7]], [85, [8]]], down),
        formatted([[85, [10, 9, 8]]], { sessionTag: OverviewSessionTag.Deload }),
        formatted([[90, [10, 9, 8]]]),
      ]),
      // #6691: long weights and 4 or 5 sets, written with the no-break spaces the real formatter uses,
      // so a phone row must wrap after a comma and never run past its card. The newest session is the
      // longest, because a phone row shows only that one
      program('hip-thrust', 'Hip Thrust', '4', '20', [
        formatted([[120, [20, 18, 18, 16]]]),
        formatted([[122, [20, 18, 18, 16]]]),
        EMPTY,
        formatted([[112.5, [22, 20, 20, 18]]], down),
        formatted([[122.5, [20, 18]], [117.5, [18, 16]]], heavy),
        formatted([[125, [20, 18, 18, 16]]]),
        formatted([[1122.5, [22, 20, 20, 18, 16]]], { sessionTag: OverviewSessionTag.Deload }),
      ]),
      program('leg-extension', 'Leg Extension', '2', '~15', [
        myo(42.5, `13${Formatting.plus}4${Formatting.plus}3${Formatting.plus}3`),
        myo(42.5, `13${Formatting.plus}4${Formatting.plus}4${Formatting.plus}3`, same),
        myo(42.5, `14${Formatting.plus}4${Formatting.plus}4${Formatting.plus}3`),
        myo(42.5, `15${Formatting.plus}5${Formatting.plus}4${Formatting.plus}3`),
        myo(45, `13${Formatting.plus}4${Formatting.plus}3${Formatting.plus}3`),
        myo(45, `14${Formatting.plus}4${Formatting.plus}3${Formatting.plus}3`),
        myo(45, `15${Formatting.plus}4${Formatting.plus}4${Formatting.plus}3`),
      ]),
      grey('leg-press', 'Leg Press', [
        EMPTY, EMPTY, EMPTY,
        formatted([[150, [10, 9, 8]]]),
        formatted([[150, [10, 9, 8]]], same),
        EMPTY, EMPTY,
      ]),
    ]),
    section(1, 'Workout B', [], [
      program('deadlift', 'Deadlift', '3', '5', []),
      program('ohp', 'Overhead Press', '3', '–', []),
    ]),
    section(undefined, 'Other sessions', ['2026-08-05', '2026-08-12'], [
      grey('pull-up', 'Pull-up', [formatted([[10, [6, 5, 5]]]), formatted([[10, [6, 6, 5]]], same)]),
      grey('deleted', 'Deleted exercise', [formatted([[20, [12, 12]]]), EMPTY], true),
    ]),
  ],
};
